// utils/fileDownloader.js
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

// ---------------------------------------------------------------
// Extract filename from Content-Disposition header
// ---------------------------------------------------------------
export const extractFileName = (response, fallbackName) => {
    const disposition =
        response?.headers?.["content-disposition"] ||
        response?.headers?.["Content-Disposition"];

    if (!disposition) return fallbackName;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const normalMatch = disposition.match(/filename="?([^"]+)"?/);
    if (normalMatch?.[1]) return normalMatch[1];

    return fallbackName;
};

// ---------------------------------------------------------------
// Loader / Success / Error toast renderers
// ---------------------------------------------------------------
const LoaderToast = ({ text }) => (
    <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-50">
            <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <span className="text-sm font-medium text-gray-800">{text}</span>
    </div>
);

const SuccessToast = ({ text }) => (
    <span className="text-sm font-medium text-gray-800">{text}</span>
);

const ErrorToast = ({ text }) => (
    <span className="text-sm font-medium text-gray-800">{text}</span>
);

// ---------------------------------------------------------------
// i18n loader messages
// ---------------------------------------------------------------
const LOADER_MESSAGES = {
    en: {
        pdf: "Generating PDF...",
        excel: "Preparing Excel file...",
        csv: "Preparing CSV file...",
        default: "Preparing your file...",
    },
    ta: {
        pdf: "PDF உருவாக்கப்படுகிறது...",
        excel: "Excel கோப்பு தயார் செய்யப்படுகிறது...",
        csv: "CSV கோப்பு தயார் செய்யப்படுகிறது...",
        default: "உங்கள் கோப்பு தயார் செய்யப்படுகிறது...",
    },
};

// ---------------------------------------------------------------
// MAIN COMMON DOWNLOAD FUNCTION
// ---------------------------------------------------------------
/**
 * Universal file download handler.
 *
 * @param {Object}   options
 * @param {Function} options.getData        - Your axios/fetch wrapper. Signature: (url, responseType) => Promise
 * @param {string}   options.url            - Base URL ending with `format=` (e.g. `/payment/receipt?order_id=X&format=`)
 * @param {'pdf'|'excel'|'csv'} options.fileType
 * @param {string}   [options.defaultFilename='Report']
 * @param {string}   [options.lang]         - 'en' | 'ta' (defaults to localStorage or 'en')
 * @returns {Promise<{success: boolean, fileName?: string, error?: string}>}
 */
export const downloadFile = async ({
    getData,
    url,
    fileType,
    defaultFilename = "Report",
    lang,
}) => {
    if (!fileType) return { success: false, error: "No file type provided" };

    const activeLang = lang || localStorage.getItem("language") || "en";
    const message =
        LOADER_MESSAGES[activeLang]?.[fileType] ||
        LOADER_MESSAGES[activeLang]?.default ||
        "Preparing file...";

    const startTime = Date.now();

    const toastId = toast(<LoaderToast text={message} />, {
        position: "top-center",
        autoClose: false,
        closeButton: false,
        draggable: false,
        className:
            "backdrop-blur-md bg-white/90 border border-gray-200 shadow-xl rounded-2xl px-4 py-3",
    });

    try {
        const responseType = ["excel", "pdf", "csv"].includes(fileType)
            ? "blob"
            : "json";

        const apiUrl = `${url}${fileType}`;
        const response = await getData(apiUrl, responseType);

        // ---- Backend error JSON ----
        if (response && typeof response === "object" && response.isError) {
            throw new Error(response.message);
        }

        if (
            !response ||
            (typeof response === "object" &&
                !Array.isArray(response) &&
                !(response instanceof Blob) &&
                (response.code || response.message))
        ) {
            throw new Error(response?.message || "Failed to download file.");
        }

        // ---- Build blob ----
        let blob;
        const fileExtension = fileType === "excel" ? "xlsx" : fileType;
        let fileName = `${defaultFilename}.${fileExtension}`;

        if (response?.headers) {
            fileName = extractFileName(response, fileName);
        }

        switch (fileType) {
            case "pdf":
            case "csv":
                blob = response.data || response;
                break;

            case "excel":
                if (response instanceof Blob || response.data instanceof Blob) {
                    blob = response.data || response;
                } else {
                    let jsonData;
                    if (Array.isArray(response)) {
                        jsonData = response;
                    } else if (response.data) {
                        jsonData =
                            typeof response.data === "string"
                                ? JSON.parse(response.data)
                                : response.data;
                    } else {
                        jsonData = response;
                    }

                    const worksheet = XLSX.utils.json_to_sheet(jsonData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

                    const excelBuffer = XLSX.write(workbook, {
                        bookType: "xlsx",
                        type: "array",
                    });

                    blob = new Blob([excelBuffer], {
                        type:
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                }
                break;

            default:
                throw new Error("Invalid file format selected");
        }

        if (!blob) throw new Error("No data available for download");

        // ---- Minimum loader time (nicer UX) ----
        const elapsed = Date.now() - startTime;
        if (elapsed < 1000) {
            await new Promise((res) => setTimeout(res, 1000 - elapsed));
        }

        // ---- Trigger download ----
        const objectURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(objectURL), 100);

        toast.update(toastId, {
            render: <SuccessToast text={`${fileName} downloaded`} />,
            type: "success",
            isLoading: false,
            autoClose: 2000,
            className:
                "backdrop-blur-md bg-green-50 border border-green-200 shadow-xl rounded-2xl px-4 py-3",
        });

        return { success: true, fileName };
    } catch (err) {
        const errorMessage = err.message || "Failed to download file.";

        toast.update(toastId, {
            render: <ErrorToast text={errorMessage} />,
            type: "error",
            isLoading: false,
            autoClose: 3000,
            className:
                "backdrop-blur-md bg-red-50 border border-red-200 shadow-xl rounded-2xl px-4 py-3",
        });

        return { success: false, error: errorMessage };
    }
};